/**
 * Defers non-critical header/footer chrome JS off the initial parse path.
 *
 * Predictive search and the newsletter signup popup are not needed for first
 * paint, so their scripts load at idle time (or earlier on interaction) instead
 * of eagerly on every page. Asset URLs are provided by an inline config block in
 * layout/theme.liquid as `window.HOG.chromeAssets`.
 *
 * Modules load via same-origin `<script type="module" src>` injection (not
 * `import(url)`). Dynamic import() fails when Hyperspeed CDN-proxies this file
 * as a cross-origin classic script (`base URL is about:blank`).
 *
 * Injected scripts set `hs-ignore` so Hyperspeed's createElement interceptor
 * does not rewrite type/src.
 *
 * `dialog.js` remains global, so the search modal still opens via its
 * `on:click="#search-modal/showDialog"` binding before predictive search upgrades.
 */
(function hogChromeIdle() {
  const assets = (window.HOG && window.HOG.chromeAssets) || {};
  const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  const requestIdle =
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (cb) => window.setTimeout(cb, 1);

  /**
   * Marks a script so Hyperspeed Smart JS Defer skips it.
   * @param {HTMLScriptElement} script
   */
  function markHsIgnore(script) {
    script.setAttribute('hs-ignore', '');
  }

  /**
   * Loads a classic (non-module) script once, deduplicated by URL.
   * @param {string} url
   * @returns {Promise<void>}
   */
  const classicScriptPromises = new Map();
  function loadClassicScript(url) {
    if (!url) return Promise.resolve();
    let promise = classicScriptPromises.get(url);
    if (promise) return promise;

    promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      markHsIgnore(script);
      script.src = url;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
    classicScriptPromises.set(url, promise);
    return promise;
  }

  /**
   * Loads an ES module via a same-origin module script tag (not import()).
   * Absolute Shopify asset URLs resolve against the document, not this file's origin.
   * @param {string} url
   * @returns {Promise<void>}
   */
  const moduleScriptPromises = new Map();
  function loadModuleScript(url) {
    if (!url) return Promise.resolve();
    let promise = moduleScriptPromises.get(url);
    if (promise) return promise;

    promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      markHsIgnore(script);
      script.type = 'module';
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load module: ${url}`));
      document.head.appendChild(script);
    });
    moduleScriptPromises.set(url, promise);
    return promise;
  }

  let tidioPromise = null;
  function loadTidio() {
    if (!assets.tidio) return Promise.resolve();
    if (!tidioPromise) {
      tidioPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        markHsIgnore(script);
        script.src = assets.tidio;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return tidioPromise;
  }

  // Storewide Tidio — inject via JS; Hyperspeed strips static third-party tags.
  loadTidio();
  loadClassicScript(assets.liveChat);

  // --- Search ---------------------------------------------------------------

  let searchPromise = null;
  function loadSearch() {
    if (!searchPromise) {
      searchPromise = Promise.all([
        assets.predictiveSearch ? loadModuleScript(assets.predictiveSearch) : Promise.resolve(),
        loadClassicScript(assets.searchTypewriter),
      ]).then(() => {
        if (!customElements.get('predictive-search-component')) {
          console.warn(
            '[HOG] predictive-search-component not defined after load. Check Hyperspeed exclusions for hog-chrome-idle.js / predictive-search.js.'
          );
        }
      });
    }
    return searchPromise;
  }

  // --- Signup popup ---------------------------------------------------------

  let signupPromise = null;
  function loadSignup() {
    if (!signupPromise) {
      signupPromise = assets.signupPopup ? loadModuleScript(assets.signupPopup) : Promise.resolve();
    }
    return signupPromise;
  }

  // --- Cart drawer ----------------------------------------------------------

  // On non-cart pages the drawer + its item modules are not needed for first
  // paint; load them on the first cart-icon interaction (or at idle).
  let cartDrawerPromise = null;
  function loadCartDrawer() {
    if (!cartDrawerPromise) {
      const urls = assets.cartDrawer || [];
      cartDrawerPromise = Promise.all(urls.map((url) => loadModuleScript(url)));
    }
    return cartDrawerPromise;
  }

  // --- Product surface (homepage) -------------------------------------------

  // Quick-add / variant-picker / product-card etc. only matter once a shopper
  // interacts with a product card, so defer the whole stack off first paint on
  // the homepage. Interacting with products implies a likely add-to-cart, so
  // warm the cart drawer at the same time.
  let productSurfacePromise = null;
  function loadProductSurface() {
    if (!productSurfacePromise) {
      const urls = assets.productSurface || [];
      productSurfacePromise = Promise.all(urls.map((url) => loadModuleScript(url)));
      loadCartDrawer();
    }
    return productSurfacePromise;
  }

  // --- Wiring ---------------------------------------------------------------

  if (isDesignMode) {
    // Theme Editor needs everything upgraded immediately for preview + section helpers.
    loadSearch();
    loadSignup();
    if (assets.cartDrawer) loadCartDrawer();
    if (assets.productSurface) loadProductSurface();
    return;
  }

  const searchTriggerHandler = (event) => {
    if (event.type === 'keydown') {
      if (!(event.metaKey || event.ctrlKey) || event.key !== 'k') return;
    } else {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.search-action, .predictive-search .search-input')) {
        return;
      }
    }
    loadSearch();
    removeSearchTriggers();
  };

  function removeSearchTriggers() {
    document.removeEventListener('pointerenter', searchTriggerHandler, true);
    document.removeEventListener('focusin', searchTriggerHandler, true);
    document.removeEventListener('click', searchTriggerHandler, true);
    document.removeEventListener('keydown', searchTriggerHandler, true);
  }

  document.addEventListener('pointerenter', searchTriggerHandler, true);
  document.addEventListener('focusin', searchTriggerHandler, true);
  document.addEventListener('click', searchTriggerHandler, true);
  document.addEventListener('keydown', searchTriggerHandler, true);

  const openSignupOnLoad = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('customer_posted') === 'true' || params.get('form_type') === 'customer';
  })();

  let signupOpenRequested = false;
  const signupTriggerHandler = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('a[href="#trigger-popup"], .modal__show')) return;
    event.preventDefault();
    signupOpenRequested = true;
    loadSignup().then(() => customElements.whenDefined('signup-popup-component')).then(() => {
      if (!signupOpenRequested) return;
      signupOpenRequested = false;
      const el = document.querySelector('signup-popup-component');
      if (el && typeof el.openPopup === 'function') el.openPopup({ force: true });
    });
    document.removeEventListener('click', signupTriggerHandler, true);
  };
  document.addEventListener('click', signupTriggerHandler, true);

  if (openSignupOnLoad) {
    loadSignup();
  }

  // Cart drawer: warm on hover/focus, and guarantee the first click opens it
  // even if the component has not upgraded yet (mirrors the signup handler).
  if (assets.cartDrawer) {
    const cartSelector = '[data-testid="cart-drawer-trigger"], cart-drawer-component';

    const cartWarmHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest(cartSelector)) return;
      loadCartDrawer();
      document.removeEventListener('pointerenter', cartWarmHandler, true);
      document.removeEventListener('focusin', cartWarmHandler, true);
    };
    document.addEventListener('pointerenter', cartWarmHandler, true);
    document.addEventListener('focusin', cartWarmHandler, true);

    const cartClickHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest('[data-testid="cart-drawer-trigger"]');
      if (!trigger) return;
      // Already upgraded: let the native on:click="/open" binding handle it.
      if (customElements.get('cart-drawer-component')) return;
      event.preventDefault();
      loadCartDrawer()
        .then(() => customElements.whenDefined('cart-drawer-component'))
        .then(() => {
          const el = trigger.closest('cart-drawer-component') || document.querySelector('cart-drawer-component');
          if (el && typeof el.open === 'function') el.open();
        });
    };
    document.addEventListener('click', cartClickHandler, true);

    requestIdle(() => loadCartDrawer(), { timeout: 5000 });
  }

  // Product surface: upgrade product cards / quick-add on the first product
  // interaction, with an idle fallback so most sessions warm before use.
  if (assets.productSurface) {
    const productSelector =
      'product-card, .product-card, quick-add-component, quick-add-dialog, .product-card__quick-add, [data-testid="add-to-cart"]';

    const productHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest(productSelector)) return;
      loadProductSurface();
      document.removeEventListener('pointerenter', productHandler, true);
      document.removeEventListener('pointerdown', productHandler, true);
      document.removeEventListener('focusin', productHandler, true);
    };
    document.addEventListener('pointerenter', productHandler, true);
    document.addEventListener('pointerdown', productHandler, true);
    document.addEventListener('focusin', productHandler, true);

    requestIdle(() => loadProductSurface(), { timeout: 4000 });
  }

  requestIdle(() => loadSearch(), { timeout: 3000 });
  requestIdle(() => loadSignup(), { timeout: 5000 });
})();
